import React, { useMemo, useState } from 'react';
import styles from './TrendingAudios.module.css';
import useUrlState from '../../../../../Hooks/useUrlState';
import {
    getTiktokAudioAssets,
    getTrendingTiktokAudioStats,
    GetTrendingTiktokAudioStatsParams,
    TiktokAudioAsset,
    usePaginatedFetch,
} from '@round/api';
import SearchInput from '../../../../../ui/DataEntry/SearchInput/SearchInput';
import { AudioPlayerProvider } from '../../../../AudioPlayer/AudioPlayerContext';
import TrendingAudiosTable from './TrendingAudiosTable/TrendingAudiosTable';
import { TrendingAudiosTableRow } from './TrendingAudios.types';
import { StylesConfig, ValueType } from 'react-select';
import { GenericDropdownOption } from '../../../../../App.types';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import debounce from 'lodash/debounce';
import Select from '../../../../../ui/DataEntry/Select/Select';
import TiktokAudioModal from '../../../audios/features/details/TiktokAudioModal/TiktokAudioModal';

type UrlState = Omit<GetTrendingTiktokAudioStatsParams, 'genre' | 'location'> &
    Partial<{
        audioModalAudio: number;
    }>;

const selectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        height: '100%',
        borderColor: '#C2CFE0',
        fontSize: '0.75rem',
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '0.75rem',
        backgroundColor: state.isSelected ? '#f7f7f7' : state.isFocused ? '#fafafa' : base.backgroundColor,
        color: 'black',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#C2CFE0',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#58698E',
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 1000,
    }),
};

const sortingOptions: GenericDropdownOption<GetTrendingTiktokAudioStatsParams['ordering']>[] = [
    { label: 'Most Recent', value: '-first_seen_at' },
    { label: 'Total Videos', value: '-video_count' },
    { label: 'Daily Growth (# videos)', value: '-video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-video_count_weekly_change_relative' },
];

const TrendingAudios = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>({
        page: 1,
        page_size: 10,
        search: '',
        ordering: '-first_seen_at',
    });

    const audioModalAudioId =
        typeof urlState.audioModalAudio === 'string' ? Number(urlState.audioModalAudio) : undefined;
    const setAudioModalAudioId = (audioId: number | undefined) => setUrlState({ audioModalAudio: audioId });

    const search = urlState.search;
    const setSearch = (search: string) => setUrlState({ search, page: 1 });
    const page = Number(urlState.page);
    const setPage = (page: number) => setUrlState({ page });
    const pageSize = Number(urlState.page_size);
    const setPageSize = (pageSize: number | undefined) => setUrlState({ page_size: pageSize ?? 10 });

    const ordering = sortingOptions.find((option) => option.value === urlState.ordering);
    const setOrdering = (
        value: ValueType<GenericDropdownOption<GetTrendingTiktokAudioStatsParams['ordering']>, false>
    ) => setUrlState({ ordering: value?.value });

    const [tableLoading, setTableLoading] = useState(false);
    const {
        page: stats,
        count,
        getCurrentPage,
    } = usePaginatedFetch(getTrendingTiktokAudioStats, {
        page,
        page_size: pageSize,
        search,
        ordering: ordering?.value,
    });

    const [assets, setAssets] = useState<TiktokAudioAsset[]>([]);

    const rows: TrendingAudiosTableRow[] = useMemo(() => {
        return stats.map<TrendingAudiosTableRow>((stats) => {
            const asset = assets.find((asset) => asset.audio_id === stats.audio_id);

            return {
                ...stats,
                audioUrl: (asset?.audio_file.cached_url || asset?.audio_file.original_url) ?? '',
                imageUrl: (asset?.image_thumb.cached_url || asset?.image_thumb.original_url) ?? '',
            };
        });
    }, [assets, stats]);

    const fetchTrendingTiktokAudioStats = useMemo(() => debounce(getCurrentPage, 700), [getCurrentPage]);

    useAbortableEffect(
        (signal) => {
            setTableLoading(true);
            const result = fetchTrendingTiktokAudioStats({ signal });
            if (result) {
                result.catch(() => setTableLoading(false));
            }
        },
        [fetchTrendingTiktokAudioStats]
    );

    useAbortableEffect(
        (signal) => {
            if (!stats.length) {
                setTableLoading(false);
                return;
            }

            getTiktokAudioAssets(
                stats.map((stats) => stats.audio_id),
                { signal }
            )
                .then(setAssets)
                .catch(() => {})
                .finally(() => setTableLoading(false));
        },
        [stats]
    );

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Trending Audios</h1>
            <div className={styles.header}>
                <div className={styles.actionsGroup}>
                    <SearchInput className={styles.search} value={search} onChange={setSearch} />
                </div>
                <div className={styles.actionsGroup}>
                    <div className={styles.ordering}>
                        <span className={styles.orderingLabel}>Sort by</span>
                        <Select
                            className={styles.orderingSelect}
                            styles={selectStyles}
                            options={sortingOptions}
                            value={ordering}
                            onChange={setOrdering}
                            menuPortalTarget={document.body}
                        />
                    </div>
                </div>
            </div>
            <AudioPlayerProvider>
                <TrendingAudiosTable
                    loading={tableLoading}
                    data={rows}
                    count={count}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    ordering={ordering?.value}
                    onRowClick={(row) => setAudioModalAudioId(row.audio_id)}
                />

                <TiktokAudioModal
                    isModalOpen={typeof audioModalAudioId === 'number'}
                    closeModal={() => setAudioModalAudioId(undefined)}
                    audioId={audioModalAudioId}
                />
            </AudioPlayerProvider>
        </div>
    );
};

export default TrendingAudios;
